import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { KadmiLogo } from '../components/KadmiLogo';
import { ArrowLeft, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import BoutonOeil from '../components/ui/BoutonOeil';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import analytics from '../services/analytics';

const V = '#7FB342';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const bg = isDark ? '#0F0F1A' : '#F7F6FF';
  const cardBg = isDark ? '#1A1A2E' : '#FFFFFF';
  const inputBg = isDark ? '#252538' : '#FFFFFF';
  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const ts = isDark ? '#9998B8' : '#6B6B8A';
  const bd = isDark ? '#2E2E45' : '#E5E3F0';
  const inputCls = `w-full h-12 pl-10 pr-12 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-[${V}]/30`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      const result = await login(email, password);
      analytics.loginCompleted(result?.user?.id || 'unknown');
      toast.success(t('auth.login_success'));
      navigate('/dashboard');
    } catch (err) {
      setError(t('auth.login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 transition-colors relative" style={{ background: bg }}>
      <Link
        to="/"
        data-testid="login-back-link"
        aria-label="Retour à l'accueil"
        className="fixed top-4 left-4 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5"
        style={{ background: cardBg, color: tp, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
      >
        <ArrowLeft className="h-5 w-5" />
      </Link>
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[420px] rounded-[20px] p-8 sm:p-10" style={{ background: cardBg, boxShadow: 'var(--shadow-card)' }}>
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <KadmiLogo size={40} />
          </Link>
          <h1 className="font-heading text-xl mt-4" style={{ color: tp }} data-testid="login-title">{t('auth.login_subtitle')}</h1>
          <p className="text-sm mt-1" style={{ color: ts }}>{t('auth.login_desc')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: tp }}>{t('auth.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: ts }} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('auth.email_placeholder')}
                className={inputCls} style={{ background: inputBg, border: `1px solid ${bd}`, color: tp }} data-testid="login-email-input" />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: tp }}>{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10" style={{ color: ts }} />
              <BoutonOeil value={password} onChange={e => setPassword(e.target.value)} placeholder={t('auth.password_placeholder')}
                className={`${inputCls} pl-10`} style={{ background: inputBg, border: `1px solid ${bd}`, color: tp }} data-testid="login-password-input" />
            </div>
            <div className="text-right mt-1.5">
              <Link to="/forgot-password" className="text-xs font-medium" style={{ color: V }}>{t('auth.forgot_password')}</Link>
            </div>
          </div>

          {/* Error */}
          {error && <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg p-2.5" data-testid="login-error">{error}</p>}

          {/* Submit */}
          <button type="submit" disabled={!email || !password || loading}
            className="w-full h-12 rounded-xl text-white font-semibold text-sm disabled:opacity-40 transition-all"
            style={{ background: V, boxShadow: `0 2px 12px rgba(30,91,224,0.3)` }} data-testid="login-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : t('auth.login_button')}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm mt-6" style={{ color: ts }}>
          {t('auth.no_account')} <Link to="/register" className="font-semibold" style={{ color: V }}>{t('auth.try_free_link')}</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default LoginPage;
